import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  IGetPassesResponse,
  IGetSearchNotesResponse,
  IGetWatchlistHeliosAppiusLiveTifResponse,
  IGetWatchlistLiveSimTifResponse,
  IFailedResponse,
  ITCPStepsResponse,
  IGMCappersResponse,
} from "../../api/compliance/index.types";
import {
  IGetNotesResponse,
  IGetTraderProfileResponse,
} from "../../api/fundedCompliance/index.types";

export interface IFundedComplianceProfileState {
  mainData: {
    data: IGetTraderProfileResponse["data"];
    isLoading: boolean;
  };
  passers: {
    data: IGetPassesResponse["data"]["results"];
    isLoading: boolean;
  };
  liveSimTif: {
    data: IGetWatchlistLiveSimTifResponse["data"]["results"];
    isLoading: boolean;
  };
  heliosAppiusKronosLiveTif: {
    data: IGetWatchlistHeliosAppiusLiveTifResponse["data"];
    isLoading: boolean;
  };
  failed: {
    data: {
      rows: IFailedResponse["data"]["results"]["rows"];
      count: number;
    };
    isLoading: boolean;
  };
  tcpSteps: {
    data: ITCPStepsResponse["data"]["results"];
    isLoading: boolean;
  };
  gauntCappers: {
    data: IGMCappersResponse["data"]["results"];
    isLoading: boolean;
  };
  watchlist_notes: {
    data: IGetSearchNotesResponse["data"];
    isLoading: boolean;
  };
  notes: {
    data: IGetNotesResponse["data"];
    isLoading: boolean;
  };
}

const initialState: IFundedComplianceProfileState = {
  mainData: {
    data: {
      trader_accounts: [],
      withdrawals: [],
      traded_accounts: [],
      pnl: [],
    },
    isLoading: true,
  },
  passers: {
    isLoading: true,
    data: {
      count: 0,
      rows: [],
    },
  },
  liveSimTif: {
    isLoading: true,
    data: {
      count: 0,
      rows: [],
    },
  },
  heliosAppiusKronosLiveTif: {
    isLoading: true,
    data: {
      count: 0,
      rows: [],
      updated: { initialization_date: "" },
    },
  },
  failed: {
    isLoading: false,
    data: {
      rows: [],
      count: 0,
    },
  },
  tcpSteps: {
    isLoading: true,
    data: {
      count: 0,
      rows: [],
    },
  },
  gauntCappers: {
    isLoading: true,
    data: {
      count: 0,
      rows: [],
    },
  },
  watchlist_notes: {
    isLoading: true,
    data: {
      results: { count: 0, rows: [] },
      updated: { measure_timestamp: "" },
    },
  },
  notes: {
    data: {
      count: 0,
      rows: [],
      active_notes_count: 0,
    },
    isLoading: false,
  },
};

const fundedComplianceProfileSlice = createSlice({
  name: "fundedComplianceProfile",
  initialState,
  reducers: {
    // MainData (Profile)
    fetchProfileRequest: (state) => {
      state.mainData.isLoading = true;
    },
    fetchProfileSuccess: (state, action: PayloadAction<IGetTraderProfileResponse["data"]>) => {
      state.mainData.isLoading = false;
      state.mainData.data = action.payload;
    },
    fetchProfileFailed: (state) => {
      state.mainData.isLoading = false;
    },

    // Passers
    fetchPassersRequest: (state) => {
      state.passers.isLoading = true;
    },
    fetchPassersSuccess: (state, action: PayloadAction<IGetPassesResponse["data"]["results"]>) => {
      state.passers.data = action.payload;
      state.passers.isLoading = false;
    },
    fetchPassersFailed: (state) => {
      state.passers.isLoading = false;
    },

    // LiveSimTif
    fetchLiveSimTifRequest: (state) => {
      state.liveSimTif.isLoading = true;
    },
    fetchLiveSimTifSuccess: (
      state,
      action: PayloadAction<IGetWatchlistLiveSimTifResponse["data"]["results"]>
    ) => {
      state.liveSimTif.data = action.payload;
      state.liveSimTif.isLoading = false;
    },
    fetchLiveSimTifFailed: (state) => {
      state.liveSimTif.isLoading = false;
    },

    // HeliosAppiusKronosLiveTif
    fetchHelAppKrLiveTifRequest: (state) => {
      state.heliosAppiusKronosLiveTif.isLoading = true;
    },
    fetchHelAppKrLiveTifSuccess: (
      state,
      action: PayloadAction<IGetWatchlistHeliosAppiusLiveTifResponse["data"]>
    ) => {
      state.heliosAppiusKronosLiveTif.data = action.payload;
      state.heliosAppiusKronosLiveTif.isLoading = false;
    },
    fetchHelAppKrLiveTifFailed: (state) => {
      state.heliosAppiusKronosLiveTif.isLoading = false;
    },

    // Failed
    fetchFailedRequest: (state) => {
      state.failed.isLoading = true;
    },
    fetchFailedSuccess: (state, action: PayloadAction<IFailedResponse["data"]["results"]>) => {
      state.failed.data = action.payload;
      state.failed.isLoading = false;
    },
    fetchFailedFailed: (state) => {
      state.failed.isLoading = false;
    },

    // TCPsteps
    fetchTCPstepsRequest: (state) => {
      state.tcpSteps.isLoading = true;
    },
    fetchTCPstepsSuccess: (state, action: PayloadAction<ITCPStepsResponse["data"]["results"]>) => {
      state.tcpSteps.data = action.payload;
      state.tcpSteps.isLoading = false;
    },
    fetchTCPstepsFailed: (state) => {
      state.tcpSteps.isLoading = false;
    },

    // GauntCappers
    fetchGauntCappersRequest: (state) => {
      state.gauntCappers.isLoading = true;
    },
    fetchGauntCappersSuccess: (
      state,
      action: PayloadAction<IGMCappersResponse["data"]["results"]>
    ) => {
      state.gauntCappers.data = action.payload;
      state.gauntCappers.isLoading = false;
    },
    fetchGauntCappersFailed: (state) => {
      state.gauntCappers.isLoading = false;
    },

    // WatchlistNotes
    fetchWatchlistNotesRequest: (state) => {
      state.watchlist_notes.isLoading = true;
    },
    fetchWatchlistNotesSuccess: (state, action: PayloadAction<IGetSearchNotesResponse["data"]>) => {
      state.watchlist_notes.data = action.payload;
      state.watchlist_notes.isLoading = false;
    },
    fetchWatchlistNotesFailed: (state) => {
      state.watchlist_notes.isLoading = false;
    },

    // Notes
    fetchNotesRequest: (state) => {
      state.notes.isLoading = true;
    },
    fetchNotesSuccess: (state, action: PayloadAction<IGetNotesResponse["data"]>) => {
      state.notes.data = action.payload;
      state.notes.isLoading = false;
    },
    fetchNotesFailed: (state) => {
      state.notes.isLoading = false;
    },
  },
});

export const fundedComplianceProfileActions = fundedComplianceProfileSlice.actions;
export default fundedComplianceProfileSlice.reducer;
